import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { maskPhoneNumber } from '../../utils/phoneValidation';
import useCountdown from '../../hooks/useCountdown';
import { REPLAY_MASK } from "../../utils/replay-mask";

// How long the code screen waits before offering Google. Most texts arrive
// within seconds, so someone still here after 30 s may never get one.
export const GOOGLE_FALLBACK_DELAY_MS = 30000;
export const USE_GOOGLE_LABEL = 'Use Google instead';

// onUseGoogle (optional): where Google sign-in lives on this page. When given,
// a "Didn't get a code? Use Google instead" line appears once the code screen
// has been up for googleFallbackDelayMs without a sign-in (resending does not
// restart the wait). Without it, no line: the screen is as before.
// useGoogleLabel: the button's words; /login says "Use Google in Safari" (or
// Chrome, or your browser) inside an in-app browser, where Google needs one.
const OTPInput = ({
  phoneNumber,
  onVerifyOTP,
  onResendOTP,
  loading,
  error,
  remainingAttempts = 3,
  resendCooldown = 60,
  onUseGoogle,
  onGoogleFallbackShown,
  useGoogleLabel = USE_GOOGLE_LABEL,
  googleFallbackDelayMs = GOOGLE_FALLBACK_DELAY_MS
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const { seconds: resendSeconds, reset: resetResendTimer } = useCountdown(resendCooldown);
  const offersGoogle = typeof onUseGoogle === 'function';
  const [showGoogleFallback, setShowGoogleFallback] = useState(false);
  // The latest callback, so a parent that re-creates it on every render does
  // not restart the wait.
  const onGoogleFallbackShownRef = useRef(onGoogleFallbackShown);
  onGoogleFallbackShownRef.current = onGoogleFallbackShown;
  const googleFallbackRef = useRef(null);
  // Read by the timer: whether any digit has been typed yet.
  const otpRef = useRef(otp);
  otpRef.current = otp;

  useEffect(() => {
    // Auto-focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // The code screen mounts once the code is sent; the wait starts there and
  // ends with it (a sign-in or a tab change unmounts this screen).
  useEffect(() => {
    if (!offersGoogle) return undefined;
    const timer = setTimeout(() => {
      setShowGoogleFallback(true);
      onGoogleFallbackShownRef.current?.();
      // On a short screen (or with the keyboard up) the line can sit below the
      // fold. Bring it into view, but only if no digit has been typed: never
      // move the page under someone entering the code.
      if (otpRef.current.every((digit) => digit === '')) {
        googleFallbackRef.current?.scrollIntoView?.({ block: 'nearest' });
      }
    }, googleFallbackDelayMs);
    return () => clearTimeout(timer);
  }, [offersGoogle, googleFallbackDelayMs]);

  useEffect(() => {
    // Clear OTP on error
    if (error) {
      setOtp(['', '', '', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [error]);

  // Distribute a multi-digit string (SMS autofill or paste) across the boxes.
  // OS one-time-code autofill delivers the whole code as typed input into the
  // focused box (not a paste event), so handleChange must handle it too.
  const distributeCode = (raw) => {
    const digits = (String(raw).match(/\d/g) || []).slice(0, 6);
    if (!digits.length) return false;
    const newOtp = ['', '', '', '', '', ''];
    digits.forEach((d, i) => { newOtp[i] = d; });
    setOtp(newOtp);
    inputRefs.current[Math.min(digits.length, 6) - 1]?.focus();
    if (digits.length === 6) {
      handleVerify(newOtp.join(''));
    }
    return true;
  };

  const handleChange = (index, value) => {
    // Autofill / fast input can deliver more than one digit into a box.
    if (value && value.length > 1) {
      distributeCode(value);
      return;
    }

    // Only allow single numbers for manual entry
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are filled
    if (newOtp.every(digit => digit !== '') && index === 5) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    distributeCode(pastedData);
  };

  const handleVerify = (otpCode = null) => {
    const otpValue = otpCode || otp.join('');

    if (otpValue.length !== 6) {
      return;
    }

    onVerifyOTP({
      phoneNumber,
      otp: otpValue
    });
  };

  const handleResend = () => {
    setOtp(['', '', '', '', '', '']);
    resetResendTimer(resendCooldown);
    onResendOTP({ phoneNumber });
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  };

  const isVerifyDisabled = otp.some(digit => digit === '') || loading;
  const isResendDisabled = resendSeconds > 0 || loading;

  return (
    <Box className="otp-input-container-wrapper">
      <Typography variant="h6" style={{ marginBottom: '10px', textAlign: 'center' }}>
        Enter Verification Code
      </Typography>

      <Typography variant="body2" style={{ marginBottom: '25px', textAlign: 'center', color: '#666' }}>
        {/* data-hj-suppress (review B2): the number the visitor typed, shown back. */}
        Sent to <strong className="masked-phone" {...REPLAY_MASK}>{maskPhoneNumber(phoneNumber)}</strong>
      </Typography>

      <Box className="otp-input-container" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
        {otp.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(ref) => (inputRefs.current[index] = ref)}
            variant="outlined"
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            inputProps={{
              // First box takes the full autofilled code (distributeCode splits
              // it); the rest stay single-char for manual entry.
              maxLength: index === 0 ? 6 : 1,
              style: { textAlign: 'center', fontSize: '24px', padding: '12px' },
              inputMode: 'numeric',
              pattern: '[0-9]*',
              // Let iOS/Android surface the SMS code for one-tap autofill on the
              // first box; distributeCode spreads it across all six.
              autoComplete: index === 0 ? 'one-time-code' : 'off'
            }}
            className="otp-digit"
            error={!!error}
          />
        ))}
      </Box>

      {error && (
        <Box className="error-message" {...REPLAY_MASK} style={{ marginBottom: '10px', color: '#d32f2f', fontSize: '14px', textAlign: 'center' }}>
          {error}
          {remainingAttempts !== undefined && remainingAttempts < 3 && (
            <Box className={`attempts-remaining ${remainingAttempts === 1 ? 'critical' : ''}`} style={{ marginTop: '5px', fontSize: '13px' }}>
              {remainingAttempts} {remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining
            </Box>
          )}
        </Box>
      )}

      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={() => handleVerify()}
        disabled={isVerifyDisabled}
        className="verify-otp-btn"
        style={{ marginBottom: '15px' }}
      >
        {loading ? (
          <>
            Verifying <CircularProgress size={20} style={{ marginLeft: '10px', color: '#fff' }} />
          </>
        ) : (
          'Verify & Continue'
        )}
      </Button>

      <Box style={{ textAlign: 'center' }}>
        {resendSeconds > 0 ? (
          <Typography variant="body2" className="countdown-timer" style={{ color: '#666' }}>
            Resend OTP in <strong>{resendSeconds}s</strong>
          </Typography>
        ) : (
          <Button
            color="secondary"
            onClick={handleResend}
            disabled={isResendDisabled}
            style={{ textTransform: 'none' }}
          >
            Didn't receive the code? Resend OTP
          </Button>
        )}
      </Box>

      {/* Polite live region, present from the start so the line is announced
          when it appears; focus stays where it is (no interruption while
          someone is typing the code). Its height is reserved from the start
          so nothing above it moves when the line appears, and it sits 12px
          below resend so a low tap on resend cannot land on it. */}
      {offersGoogle && (
        <Box
          ref={googleFallbackRef}
          className="otp-google-fallback"
          aria-live="polite"
          style={{ textAlign: 'center', marginTop: '12px', minHeight: '36px' }}
        >
          {showGoogleFallback && (
            <Typography variant="body2" component="p" style={{ color: '#666', margin: 0 }}>
              Didn't get a code?{' '}
              <Button
                color="secondary"
                onClick={onUseGoogle}
                className="otp-use-google-btn"
                style={{ textTransform: 'none' }}
              >
                {useGoogleLabel}
              </Button>
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default OTPInput;
